import { CustomAxios } from "@shared/api/axiosClient";

import type { Schemas } from "@shared/api/types";

export type PreInvoice = Schemas["PreInvoiceDTO"];
export type PreInvoiceDetail = Schemas["PreInvoiceDetailDTO"];
export type Invoice = Schemas["InvoiceDTO"];
export type UserProfile = Schemas["UserProfileDTO"];
export type AddItemRequest = Schemas["AddItemPreInvoiceDTO"];
export type ConfirmPreInvoiceRequest = Schemas["ConfirmPreInvoiceRequest"];
export type ValidateHours = Schemas["ValidateHoursDTO"];
export type ValidateLastMonth = Schemas["ValidatePreInvoiceLastMonthDTO"];

/** Lista de prefacturas del colaborador. */
export const getPreInvoiceList = (): Promise<PreInvoice[]> =>
  CustomAxios.get("pre-invoices/pre-invoice-list").then(({ data }) => data);

/** Perfil del usuario logueado (para nombre de archivo y email de período). */
export const getUserProfile = (): Promise<UserProfile> =>
  CustomAxios.get("user-profile").then(({ data }) => data);

/** Cancela una prefactura. */
export const cancelPreInvoice = (preInvoiceId: number): Promise<string> =>
  CustomAxios.get(`pre-invoices/cancel-pre-invoice/${preInvoiceId}`).then(({ data }) => data);

/** Descarga una prefactura como blob (devuelve la respuesta completa). */
export const downloadPreInvoice = (
  preInvoiceId: number
): Promise<{ data: Blob; headers: Record<string, string> }> =>
  CustomAxios.get(`pre-invoices/${preInvoiceId}/download`, { responseType: "blob" });

/** Prefactura actual del colaborador. */
export const getCurrentPreInvoice = (): Promise<PreInvoice> =>
  CustomAxios.get("pre-invoices/current").then(({ data }) => data);

/** Genera/obtiene la prefactura del mes actual. */
export const createPreInvoice = (): Promise<PreInvoice> =>
  CustomAxios.get("pre-invoices").then(({ data }) => data);

/**
 * Calcula la prefactura del mes actual EN MEMORIA, sin persistir nada (preview del Camino A).
 * La persistencia real ocurre recién al confirmar.
 */
export const previewPreInvoice = (): Promise<PreInvoice> =>
  CustomAxios.get("pre-invoices/preview").then(({ data }) => data);

/**
 * Confirma la prefactura del mes actual: persiste + agrega los conceptos adicionales + genera
 * la factura, todo atómico en el backend. Solo se envían los conceptos adicionales; los importes
 * de las asignaciones los recalcula el servidor.
 */
export const confirmCurrentMonthInvoice = (additionalItems: PreInvoiceDetail[] = []): Promise<Invoice> =>
  CustomAxios.post("invoices/confirm-pre-invoice", { additionalItems }).then(({ data }) => data);

/** Genera/obtiene la prefactura del mes anterior. */
export const createPreInvoiceLastMonth = (): Promise<PreInvoice> =>
  CustomAxios.get("pre-invoices/create-last-month").then(({ data }) => data);

/** Obtiene la prefactura para un email + mes/año específicos. */
export const getPreInvoiceByEmailAndMonth = (email: string, month: number, year: number): Promise<PreInvoice> =>
  CustomAxios.get("pre-invoices/by-email-and-month", {
    params: { email, month, year },
  }).then(({ data }) => data);

/** Valida las horas cargadas antes de generar la prefactura. */
export const validatePreInvoiceHours = (): Promise<ValidateHours> =>
  CustomAxios.get("pre-invoices/validate-hours").then(({ data }) => data);

/** Valida si hay datos del mes anterior antes de generar la prefactura. */
export const validatePreInvoiceLastMonth = (): Promise<ValidateLastMonth> =>
  CustomAxios.get("pre-invoices/validate-last-month").then(({ data }) => data);

/** Agrega ítems adicionales a la prefactura. La url varía según el consumidor. */
export const addPreInvoiceItem = (url: string, request: AddItemRequest): Promise<PreInvoice> =>
  CustomAxios.post(url, request).then(({ data }) => data);

/** Confirma una prefactura para generar la factura. La url varía según el consumidor. */
export const confirmPreInvoice = (url: string, preInvoiceId: number): Promise<Invoice> =>
  CustomAxios.get(`${url}${preInvoiceId}`).then(({ data }) => data);

/** Envía la factura. La url varía según el consumidor (invoices/send o admin/...). */
export const sendInvoiceRequest = (url: string, request: unknown): Promise<unknown> =>
  CustomAxios.post(url, request).then(({ data }) => data);
